// TODO: REPLACE THESE WITH THE NEW BUTTONS COMPONENT FROM UI LIBRARY

import React from "react";
import styled from "@emotion/styled";
import Spinner from "./Spinner";
import {
  BUTTON_BOX_SHADOW,
  BUTTON_PRIMARY_BORDER,
  GHOST_BACKGROUND,
  GHOST_BACKGROUND_PRESSED,
  TERTIARY_GREY,
  QUATERNARY_GREY,
  DISABLED_COLOR,
  DISABLED_BACKGROUND,
  OLD_BLUE,
  DELETE,
  DELETE_PRESSED,
  LIGHT_FONT,
} from "src/constants/colors";
import { PRIMARY_BG_GRADIENT } from "src/constants/styles";

export interface ButtonProps {
  width?: string;
  height?: string;
  padding?: string;
  fontSize?: string;
  variant?: "primary" | "secondary" | "ghost" | "delete";
  size?: "large" | "icon" | "auto";
  initialBg?: boolean;
  loading?: boolean;
  disabled?: boolean;
  selected?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}

interface ButtonStates {
  default: string;
  hover: string;
  focus: string;
  active: string;
  disabled: string;
}

const getWidth = (width?: string, size?: string) => {
  if (size === "icon") return "34px";
  if (width) return width;
  return "auto";
};

const getHeight = (size?: string) => {
  switch (size) {
    case "large":
      return "48px";
    case "icon":
      return "34px";
    default:
      return "auto";
  }
};

const getPadding = (size?: string) => {
  switch (size) {
    case "large":
      return "8px 14px 8px 14px";
    case "icon":
      return "8px";
    default:
      return "8px 14px 8px 14px";
  }
};

const getColor = (variant?: string): ButtonStates => {
  switch (variant) {
    case "ghost":
      return {
        default: "#687083",
        hover: "#1453F5",
        focus: "#1453F5",
        active: "#1453F5",
        disabled: "#AEB3BB",
      };
    default:
      return {
        default: "white",
        hover: "white",
        focus: "white",
        active: "white",
        disabled: "#AEB3BB",
      };
  }
};

const getBackgroundColor = (variant?: string): ButtonStates => {
  switch (variant) {
    case "ghost":
      return {
        default: "transparent",
        hover: "#E5EBFD",
        focus: "#E5EBFD",
        active: "#DDE3F5",
        disabled: "#EFF2F6",
      };
    default:
      return {
        default: "transparent",
        hover: "transparent",
        focus: "transparent",
        active: "transparent",
        disabled: "transparent",
      };
  }
};

const shouldForwardProp = (prop: string) =>
  !["loading", "initialBg", "variant", "size", "selected"].includes(prop);

export const ButtonBase = styled("button", { shouldForwardProp })<ButtonProps>`
  width: ${({ width, size }) => getWidth(width, size)};
  height: ${({ size }) => getHeight(size)};
  font-size: 16px;
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 40px;
  padding: ${({ size }) => getPadding(size)};
  cursor: pointer;
  outline: none;
  transition: background-color 0.15s linear;
  color: ${({ variant }) => getColor(variant).default};
  background-color: ${({ variant }) => getBackgroundColor(variant).default};

  &:hover {
    color: ${({ variant }) => getColor(variant).hover};
    background-color: ${({ variant }) => getBackgroundColor(variant).hover};
  }

  &:focus {
    color: ${({ variant }) => getColor(variant).focus};
    background-color: ${({ variant }) => getBackgroundColor(variant).focus};
    box-shadow: inset 0 0 0 3px #1453f533;
  }

  &:active {
    color: ${({ variant }) => getColor(variant).active};
    background-color: ${({ variant }) => getBackgroundColor(variant).active};
  }

  &:disabled {
    color: ${({ variant }) => getColor(variant).disabled};
    background-color: ${({ variant }) => getBackgroundColor(variant).disabled};
    cursor: not-allowed;
  }
`;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      size = "auto",
      width = "auto",
      initialBg = false,
      loading,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <ButtonBase
        ref={ref}
        type="button"
        size={size}
        width={width}
        initialBg={initialBg}
        disabled={disabled}
        loading={loading}
        {...props}
      >
        {loading ? <Spinner /> : children}
      </ButtonBase>
    );
  },
);

export const PrimaryButton = styled(Button)<ButtonProps>`
  border: ${({ disabled, loading }) =>
    disabled || loading ? TERTIARY_GREY : `1px solid ${BUTTON_PRIMARY_BORDER}`};
  background: ${({ loading }) =>
    loading ? TERTIARY_GREY : PRIMARY_BG_GRADIENT};
  color: white;
  box-shadow: inset 0px 1px 0px 1px ${BUTTON_BOX_SHADOW};

  ${({ disabled }) =>
    !disabled &&
    `
      &:hover, &:active {
        outline: 4px solid ${DISABLED_BACKGROUND};
      }
    `}
`;

export const SecondaryButton = styled(Button)<ButtonProps>`
  background-color: rgba(239, 242, 246, 1);
  border: 0;
  color: ${({ disabled }) => (disabled ? DISABLED_COLOR : QUATERNARY_GREY)};
  font-size: 14px;

  ${({ disabled }) =>
    !disabled &&
    `
      &:hover {
        color: ${OLD_BLUE};
        background-color: ${GHOST_BACKGROUND};
      }
      &:active {
        color: ${OLD_BLUE};
        background-color: ${GHOST_BACKGROUND_PRESSED};
      }
    `}
`;

export const GhostButton = styled(SecondaryButton)<ButtonProps>`
  background-color: ${({ loading }) =>
    loading ? TERTIARY_GREY : "transparent"};

  ${({ selected }) =>
    selected &&
    `
      color: ${OLD_BLUE};
      background-color: ${GHOST_BACKGROUND};
    `}
`;

export const DeleteButton = styled(Button)<ButtonProps>`
  gap: 6px;
  color: ${({ initialBg }) => (initialBg ? LIGHT_FONT : DELETE)};
  border: 0;
  font-size: 14px;
  background: ${({ initialBg }) => (initialBg ? DELETE : "transparent")};

  &:hover {
    background: ${({ initialBg }) => (initialBg ? DELETE_PRESSED : DELETE)};
    color: ${({ initialBg }) => !initialBg && LIGHT_FONT};
  }

  &:active {
    background: ${DELETE_PRESSED};
  }
`;
